import { useEffect, useRef, useState } from "react";

/**
 * Animated counter that runs once when the element enters the viewport.
 * Props: value (number), prefix, suffix, decimals, duration (ms)
 */
export default function AnimatedCounter({
  value = 0,
  prefix = "",
  suffix = "",
  decimals = 0,
  duration = 1800,
  className = "",
  "data-testid": testId,
}) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let started = false;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          started = true;
          const start = performance.now();
          const tick = (now) => {
            const t = Math.min(1, (now - start) / duration);
            // easeOutCubic
            const eased = 1 - Math.pow(1 - t, 3);
            setDisplay(value * eased);
            if (t < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [value, duration]);

  const formatted = display.toLocaleString("fr-FR", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return (
    <span ref={ref} data-testid={testId} className={className}>
      {prefix}{formatted}{suffix}
    </span>
  );
}
